/**
 * StatsScreen — Player statistics overlay.
 *
 * Shows: time played, blocks mined/placed, mobs killed, distance walked,
 * deaths, dimensions visited, food eaten.
 *
 * Accessible via /stats command or from PauseMenu.
 */

export class StatsScreen {
  constructor() {
    this.isOpen = false;
    this.stats = {
      timePlayed: 0,        // seconds
      blocksMined: 0,
      blocksPlaced: 0,
      mobsKilled: 0,
      deaths: 0,
      distanceWalked: 0,    // blocks
      foodEaten: 0,
      jumps: 0,
      damageDealt: 0,
      damageTaken: 0,
      fishCaught: 0,
      tradesCompleted: 0,
      dimensionsVisited: new Set(['overworld']),
    };
    this._lastPos = null;
    this._buildUI();
  }

  _buildUI() {
    this._overlay = document.createElement('div');
    this._overlay.style.cssText = `
      position:fixed; top:0; left:0; width:100%; height:100%;
      background:rgba(0,0,0,0.75); z-index:910; display:none;
      justify-content:center; align-items:center;
    `;

    const panel = document.createElement('div');
    panel.style.cssText = `
      background:#2A2A2A; border:4px solid; border-color:#4A4A4A #1A1A1A #1A1A1A #4A4A4A;
      padding:24px 32px; min-width:400px; max-width:500px;
      font-family:'Courier New',monospace; color:#DDD;
    `;

    const title = document.createElement('div');
    title.textContent = 'Estadísticas';
    title.style.cssText = 'font-size:22px;font-weight:bold;color:#FFD700;text-shadow:2px 2px 0 #3F3F3F;margin-bottom:16px;text-align:center;';
    panel.appendChild(title);

    this._statsContainer = document.createElement('div');
    this._statsContainer.style.cssText = 'display:flex;flex-direction:column;gap:6px;';
    panel.appendChild(this._statsContainer);

    const closeBtn = document.createElement('div');
    closeBtn.textContent = 'Cerrar';
    closeBtn.style.cssText = `
      margin-top:16px; padding:8px 0; text-align:center; cursor:pointer;
      background:#555; border:2px solid; border-color:#777 #333 #333 #777;
      font-size:14px; font-weight:bold; color:#EEE;
    `;
    closeBtn.addEventListener('click', () => this.hide());
    closeBtn.addEventListener('mouseenter', () => { closeBtn.style.background = '#666'; });
    closeBtn.addEventListener('mouseleave', () => { closeBtn.style.background = '#555'; });
    panel.appendChild(closeBtn);

    this._overlay.appendChild(panel);
    document.body.appendChild(this._overlay);
  }

  show() {
    this._render();
    this._overlay.style.display = 'flex';
    this.isOpen = true;
  }

  hide() {
    this._overlay.style.display = 'none';
    this.isOpen = false;
  }

  _render() {
    const s = this.stats;
    const hours = Math.floor(s.timePlayed / 3600);
    const mins = Math.floor((s.timePlayed % 3600) / 60);
    const secs = Math.floor(s.timePlayed % 60);

    const rows = [
      ['⏱ Tiempo Jugado', `${hours}h ${mins}m ${secs}s`],
      ['⛏ Bloques Minados', s.blocksMined.toLocaleString()],
      ['🧱 Bloques Colocados', s.blocksPlaced.toLocaleString()],
      ['⚔ Mobs Eliminados', s.mobsKilled.toLocaleString()],
      ['💀 Muertes', s.deaths.toLocaleString()],
      ['🚶 Distancia Caminada', `${Math.floor(s.distanceWalked).toLocaleString()} bloques`],
      ['🍖 Comida Consumida', s.foodEaten.toLocaleString()],
      ['🦘 Saltos', s.jumps.toLocaleString()],
      ['🗡 Daño Infligido', s.damageDealt.toLocaleString()],
      ['💔 Daño Recibido', s.damageTaken.toLocaleString()],
      ['🐟 Peces Pescados', s.fishCaught.toLocaleString()],
      ['🤝 Comercios', s.tradesCompleted.toLocaleString()],
      ['🌍 Dimensiones', Array.from(s.dimensionsVisited).join(', ')],
    ];

    this._statsContainer.innerHTML = '';
    for (const [label, value] of rows) {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px solid #3A3A3A;';
      row.innerHTML = `<span style="color:#AAA">${label}</span><span style="color:#FFF;font-weight:bold">${value}</span>`;
      this._statsContainer.appendChild(row);
    }
  }

  // Called every frame to track distance and time
  update(dt, playerPos) {
    this.stats.timePlayed += dt;
    if (this._lastPos && playerPos) {
      const dx = playerPos.x - this._lastPos.x;
      const dz = playerPos.z - this._lastPos.z;
      this.stats.distanceWalked += Math.sqrt(dx * dx + dz * dz);
    }
    if (playerPos) {
      this._lastPos = { x: playerPos.x, z: playerPos.z };
    }
  }

  // Event trackers
  onBlockMined() { this.stats.blocksMined++; }
  onBlockPlaced() { this.stats.blocksPlaced++; }
  onMobKilled() { this.stats.mobsKilled++; }
  onDeath() { this.stats.deaths++; }
  onFoodEaten() { this.stats.foodEaten++; }
  onJump() { this.stats.jumps++; }
  onDamageDealt(amount) { this.stats.damageDealt += amount; }
  onDamageTaken(amount) { this.stats.damageTaken += amount; }
  onFishCaught() { this.stats.fishCaught++; }
  onTradeCompleted() { this.stats.tradesCompleted++; }
  onDimensionVisited(dim) { this.stats.dimensionsVisited.add(dim); }

  serialize() {
    return {
      ...this.stats,
      dimensionsVisited: Array.from(this.stats.dimensionsVisited),
    };
  }

  deserialize(data) {
    if (!data) return;
    Object.assign(this.stats, data);
    if (data.dimensionsVisited) {
      this.stats.dimensionsVisited = new Set(data.dimensionsVisited);
    }
  }
}
